import { inject, Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { from, NEVER, Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { ActiveSecurityContextStateService } from '../active-security/active-security-context.state-service';
import { ActiveAuthenticationStateService } from '@auth-n/state/active-authentication.state-service';
import { AuthenticationService } from '@auth-n/authentication.service';
import { cintraApiAuthHeaders } from './cintra-api-auth-headers';
import { isCloudApiUrl } from './utils/interceptor-utils';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private readonly authenticationState = inject(ActiveAuthenticationStateService);
  private readonly securityStateService = inject(ActiveSecurityContextStateService);
  // AuthenticationService needs HttpClient, which needs this interceptor: resolved on use to avoid the cycle.
  private readonly injector = inject(Injector);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.authenticationState.isAuthenticated) return next.handle(request);
    if (!isCloudApiUrl(request)) return next.handle(request);

    return from(cintraApiAuthHeaders(this.securityStateService, request.headers)).pipe(
      mergeMap((headers) =>
        headers
          ? next.handle(request.clone({ withCredentials: false, setHeaders: headers }))
          : this.redirectStaleSessionToSignIn(request, next)
      )
    );
  }

  private redirectStaleSessionToSignIn(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authenticationService = this.injector.get(AuthenticationService);
    if (!authenticationService.isSignOutInProgress && this.authenticationState.isAuthenticated) {
      authenticationService.redirectToSignInAfterSessionExpiry();
    }
    return authenticationService.canNavigateToSignIn ? NEVER : next.handle(request);
  }
}
